import React, { FC } from 'react'
import { Field, FieldProps, Form } from 'formik'
import { t } from '@lingui/macro'
import {
  FormInput,
  FormSelect,
  Button,
  NextIcon
} from 'pages/native/onBoarding/components'
import styles from './SignupFirstStepForm.module.scss'
import { PhoneNumberField } from 'pages/native/onBoarding/components/PhoneInput/PhoneNumberField/PhoneNumberField'
import { Country } from 'api/countries/getCountriesList'

interface Props {
  formik: any
  onBackToLogin: () => void
}

export const SignupFirstStepForm: FC<Props> = ({ formik, onBackToLogin }) => {
  return (
    <Form>
      <div className={styles.FormContainer}>
        <div className={styles.FormFields}>
          <Field
            component={FormInput}
            id="firstName"
            data-testid="first-name-input"
            label={t`First name`}
            name="firstName"
            isRequired
            placeholder={t`Eg: John`}
            value={formik.values.firstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formik.handleChange(e)
              formik.validateField('firstName')
            }}
            onBlur={formik.handleBlur}
            isValid={!(formik.touched.firstName && formik.errors.firstName)}
            validationMessage={formik.errors.firstName}
          />

          <Field
            component={FormInput}
            id="lastName"
            data-testid="last-name-input"
            label={t`Last name`}
            name="lastName"
            isRequired
            placeholder={t`Eg: Doe`}
            value={formik.values.lastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              formik.handleChange(e)
              formik.validateField('lastName')
            }}
            onBlur={formik.handleBlur}
            isValid={!(formik.touched.lastName && formik.errors.lastName)}
            validationMessage={formik.errors.lastName}
          />

          <PhoneNumberField
            label={`${t`Phone number`} *`}
            id={'phone'}
            placeholder={'733 73 73'}
            onCountrySelected={(country: Country) => {
              formik.setFieldValue('countryCode', country.countryCode)
              formik.setFieldTouched('country', true, false)
            }}
            isValid={!(formik.touched.phone && formik.errors.phone)}
            validationMessage={formik.errors.phone}
          />

          <div className={styles.FlexContainer}>
            <Field name="gender">
              {({ field, form }: FieldProps) => (
                <FormSelect
                  id="gender"
                  name={field.name}
                  label={t`Gender`}
                  field={field}
                  form={form}
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' }
                  ]}
                  placeholder={t`Please select one`}
                  isRequired
                  error={
                    form.touched.gender &&
                    typeof form.errors.gender === 'string'
                      ? form.errors.gender
                      : undefined
                  }
                />
              )}
            </Field>

            <Field name="birthYear">
              {({ field, form }: FieldProps) => (
                <FormSelect
                  id="birthYear"
                  name={field.name}
                  label={t`Birth Year`}
                  field={field}
                  form={form}
                  options={Array.from({ length: 75 }, (_, i) => {
                    const year = 1950 + i
                    return { value: String(year), label: String(year) }
                  })}
                  placeholder={t`Please select one`}
                  isRequired
                  error={
                    form.touched.birthYear &&
                    typeof form.errors.birthYear === 'string'
                      ? form.errors.birthYear
                      : undefined
                  }
                />
              )}
            </Field>
          </div>
        </div>

        <div className={styles.ButtonContainer}>
          <Button
            type="submit"
            label={t`Next`}
            icon={<NextIcon />}
            iconPosition="right"
            variant="filled"
            theme="primary"
            disabled={!formik.isValid || formik.isSubmitting}
          />

          <Button
            variant="text"
            theme="support"
            textAlign="left"
            onClick={() => {
              window.open(
                'https://www.solidsport.com/privacy-policy/',
                '_blank'
              )
            }}
            label={
              <div className={styles.TextInline}>
                <span>
                  {t`Your data is safe with Solidsport. We’re GDPR compliant. Please read our`}
                  &nbsp;
                  <span
                    className={styles.ClickableText}
                  >{t`Privacy Policy here.`}</span>
                </span>
              </div>
            }
          />

          <Button
            variant="text"
            theme="support"
            textAlign="left"
            onClick={onBackToLogin}
            label={
              <div className={styles.TextInline}>
                <span>{t`Do you already have an account?`} </span>
                <span className={styles.ClickableText}>{t`Login here.`}</span>
              </div>
            }
          />
        </div>
      </div>
    </Form>
  )
}
